/**
 * Λίστα του επιλεγμένου κόμβου: σειρά = σειρά στο ταμείο (σύρσιμο), «Διαθέσιμο» και «Κρυφό»
 * αποθηκεύονται ΑΜΕΣΑ (απόφαση ιδιοκτήτη 12/10/2026 — το «τελείωσε» μέσα στη βάρδια), όλα τα
 * υπόλοιπα περνούν από το πρόχειρο. Η αναζήτηση ψάχνει σε όλο τον κατάλογο.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Eye, EyeOff, GripVertical, ListFilter, Minus, Package, Plus, Search, X } from 'lucide-react';
import { cx, MenuButton, Switch, ToneDot } from './ui';
import { ProductView, ListFilter as Filter, channelSummary, priceSummary } from './catalogView';
import type { DragState } from './dragState';
import { dropHalf } from './dragState';

export interface ListSection {
  /** κόμβος κατηγορίας των γραμμών ('none' για χωρίς κατηγορία, '' για αναζήτηση/«Όλα») */
  node: string;
  title?: string;
  tone?: string | null;
  count?: number;
  rows: ProductView[];
  empty?: string;
}

export interface ProductListProps {
  title: string;
  crumb: React.ReactNode;
  count: number;
  tone?: string | null;
  chooser: boolean;
  onOpenChooser: () => void;
  sections: ListSection[];
  showContext: boolean;
  contextOf: (v: ProductView) => string;
  groupsOf: (v: ProductView) => string | null;
  edited: Set<string>;
  selectedKey: string | null;
  checked: string[];
  search: string;
  onSearch: (q: string) => void;
  filter: Filter;
  onFilter: (f: Filter) => void;
  readOnly: boolean;
  lockTitle?: string;
  canAdd: boolean;
  onAdd: () => void;
  onOpen: (key: string) => void;
  onToggleCheck: (key: string) => void;
  onSetChecked: (keys: string[]) => void;
  onToggleAvailable: (key: string) => void;
  onToggleHidden: (key: string) => void;
  busy: Set<string>;
  reorderable: boolean;
  onReorder: (node: string, keys: string[]) => void;
  /** μετακίνηση σε άλλη κατηγορία, στη θέση index της λίστας της */
  onMoveTo: (keys: string[], node: string, index: number) => void;
  drag: DragState;
  footer: { total: number; unavailable: number; hidden: number };
}

const ProductList: React.FC<ProductListProps> = props => {
  const { t } = useTranslation(['catalog']);
  const { sections, checked, readOnly, drag } = props;
  const [over, setOver] = useState<{ key: string; pos: 'before' | 'after' } | null>(null);
  const [dragging, setDragging] = useState<string[]>([]);

  const visibleKeys = sections.flatMap(s => s.rows.map(r => r.key));
  const allChecked = visibleKeys.length > 0 && visibleKeys.every(k => checked.includes(k));
  const someChecked = checked.length > 0;

  const priceLabels = {
    fee: t('catalog:list.feeProduct'), openPrice: t('catalog:list.openPrice'), noSizes: t('catalog:list.noSizes'),
    sizes: (n: number) => t('catalog:list.sizesCount', { count: n }),
  };
  const channelLabels = {
    takeAway: t('catalog:sizes.takeAway'), dineIn: t('catalog:sizes.dineIn'), delivery: t('catalog:sizes.delivery'), perSize: t('catalog:list.perSize'),
  };

  const join = (parts: React.ReactNode[]) => {
    const items = parts.filter(Boolean);
    return items.map((p, i) => (
      <React.Fragment key={i}>{i > 0 && <span className="cx-ds">·</span>}{p}</React.Fragment>
    ));
  };

  const onRowDragStart = (e: React.DragEvent, section: ListSection, v: ProductView) => {
    const keys = checked.includes(v.key) ? checked.slice() : [v.key];
    drag.start({ kind: 'products', keys, node: section.node });
    setDragging(keys);
    try { e.dataTransfer.setData('text/plain', keys.join(',')); e.dataTransfer.effectAllowed = 'move'; } catch { /* jsdom */ }
  };

  const onRowDrop = (e: React.DragEvent, section: ListSection, v: ProductView) => {
    e.preventDefault();
    const d = drag.current;
    const pos = over && over.key === v.key ? over.pos : dropHalf(e);
    setOver(null);
    setDragging([]);
    drag.end();
    if (!d || d.kind !== 'products' || readOnly || !props.reorderable) return;
    const moving = d.keys.filter(k => k !== v.key);
    if (moving.length === 0) return;
    const rest = section.rows.map(r => r.key).filter(k => !moving.includes(k));
    let at = rest.indexOf(v.key);
    if (at < 0) at = rest.length;
    if (pos === 'after') at += 1;
    if (d.node === section.node && d.keys.every(k => section.rows.some(r => r.key === k))) {
      const next = rest.slice();
      next.splice(at, 0, ...moving);
      props.onReorder(section.node, next);
    } else {
      props.onMoveTo(moving, section.node, at);
    }
  };

  const onRowKeyDown = (e: React.KeyboardEvent, section: ListSection, index: number) => {
    if (!e.altKey || readOnly || !props.reorderable || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    e.preventDefault();
    const keys = section.rows.map(r => r.key);
    const to = index + (e.key === 'ArrowUp' ? -1 : 1);
    if (to < 0 || to >= keys.length) return;
    const next = keys.slice();
    const [item] = next.splice(index, 1);
    next.splice(to, 0, item);
    props.onReorder(section.node, next);
  };

  const row = (section: ListSection, v: ProductView, index: number) => {
    const isChecked = checked.includes(v.key);
    const sel = !someChecked && props.selectedKey === v.key;
    const drop = over && over.key === v.key ? over.pos : null;
    const statuses: React.ReactNode[] = [
      v.isNew ? <span className="cx-st-new">{t('catalog:status.new')}</span> : null,
      !v.is_available ? <span className="cx-st-off">{t('catalog:status.unavailable')}</span> : null,
      v.is_hidden ? <span className="cx-st-hid">{t('catalog:status.hidden')}</span> : null,
    ];
    const ctx = props.showContext ? <span className="cx-ctx">{props.contextOf(v)}</span> : null;
    const price = <span className="cx-num">{priceSummary(v, priceLabels)}</span>;
    const channels = channelSummary(v.sizes, channelLabels);
    const groups = props.groupsOf(v);
    const canDrag = props.reorderable && !readOnly;
    return (
      <div
        key={v.key}
        role="listitem"
        data-testid={`product-row-${v.key}`}
        className={cx('cx-prow', sel && 'sel', isChecked && 'checked', !v.is_available && 'off', v.is_hidden && 'hid', drop && `drop-${drop}`, dragging.includes(v.key) && 'dragging')}
        draggable={canDrag}
        onDragStart={canDrag ? e => onRowDragStart(e, section, v) : undefined}
        onDragEnd={() => { drag.end(); setDragging([]); setOver(null); }}
        onDragOver={canDrag ? e => {
          if (drag.current?.kind !== 'products') return;
          e.preventDefault();
          setOver({ key: v.key, pos: dropHalf(e) });
        } : undefined}
        onDragLeave={() => setOver(o => (o && o.key === v.key ? null : o))}
        onDrop={canDrag ? e => onRowDrop(e, section, v) : undefined}
        onClick={() => props.onOpen(v.key)}
      >
        {v.color && <span className="cx-bar catalog-tone" data-tone={v.color} aria-hidden="true" />}
        {canDrag && (
          <button type="button" className="cx-grip" aria-label={t('catalog:list.reorderRow', { name: v.name })}
            title={t('catalog:list.reorderHint')} onClick={e => e.stopPropagation()} onKeyDown={e => onRowKeyDown(e, section, index)}>
            <GripVertical className="cx-ico" />
          </button>
        )}
        <input
          type="checkbox"
          className="cx-cb"
          checked={isChecked}
          aria-label={t('catalog:list.select', { name: v.name || t('catalog:product.untitled') })}
          onClick={e => e.stopPropagation()}
          onChange={() => props.onToggleCheck(v.key)}
        />
        <button type="button" className="cx-pmain" onClick={e => { e.stopPropagation(); props.onOpen(v.key); }} aria-label={t('catalog:list.open', { name: v.name || t('catalog:product.untitled') })}>
          <span className="cx-pname">
            <span className="cx-pn" data-testid="product-name">{v.name || t('catalog:product.untitled')}</span>
            {props.edited.has(v.key) && <span className="cx-edot" title={t('catalog:list.unsaved')} />}
          </span>
          <span className="cx-psub compact">{join([ctx, ...statuses, price])}</span>
          <span className="cx-psub wide">{join([ctx, ...statuses, v.description || (statuses.some(Boolean) || ctx ? null : <span style={{ opacity: 0.7 }}>{t('catalog:list.noDescription')}</span>)])}</span>
        </button>
        <div className="cx-c-sizes">
          <div className="cx-schips">
            {v.feeCategory !== null || v.openPrice || v.sizes.length === 0 ? (
              <span className="cx-schip"><b>{priceSummary(v, priceLabels)}</b></span>
            ) : v.sizes.length === 1 && v.sizes[0].name === 'Κανονικό' ? (
              <span className={cx('cx-schip', v.sizes[0].is_hidden && 'hidden-size')}><b>{priceSummary(v, priceLabels)}</b></span>
            ) : v.sizes.map(s => (
              <span key={s._uid} className={cx('cx-schip', (s.is_hidden || s.is_available === false) && 'hidden-size')}>
                {s.name} <b>{s.price_modifier.toFixed(2).replace('.', ',')}</b>
              </span>
            ))}
          </div>
          {channels && <div className="cx-chsum" title={t('catalog:list.channelPrices')}>{channels}</div>}
        </div>
        <div className="cx-c-groups">{groups ?? '—'}</div>
        <span className="cx-c-av">
          <Switch
            checked={v.is_available}
            pending={v.pendingAvailable}
            disabled={props.busy.has(v.key)}
            label={t('catalog:list.availableToggle', { name: v.name })}
            title={v.is_available ? t('catalog:status.available') : t('catalog:status.unavailable')}
            onChange={() => props.onToggleAvailable(v.key)}
          />
        </span>
        <span className="cx-c-eye">
          <button
            type="button"
            className={cx('cx-eye', v.pendingHidden && 'pending')}
            aria-pressed={v.is_hidden}
            aria-label={t('catalog:list.hiddenToggle', { name: v.name })}
            title={v.is_hidden ? t('catalog:list.hiddenTitle') : t('catalog:list.visibleTitle')}
            disabled={props.busy.has(v.key)}
            onClick={e => { e.stopPropagation(); props.onToggleHidden(v.key); }}
          >
            {v.is_hidden ? <EyeOff className="cx-ico" /> : <Eye className="cx-ico" />}
          </button>
        </span>
      </div>
    );
  };

  const filterLabels: Record<Filter, string> = {
    all: t('catalog:filter.all'), available: t('catalog:filter.available'), unavailable: t('catalog:filter.unavailable'), hidden: t('catalog:filter.hidden'),
  };

  const searching = props.search.trim() !== '';
  const noRows = visibleKeys.length === 0;

  return (
    <section className="cx-pane cx-list" aria-label={t('catalog:list.title')}>
      <div className="cx-lp-head">
        <div className="cx-lp-title">
          {props.chooser ? (
            <button type="button" className="cx-chooser" onClick={props.onOpenChooser} title={t('catalog:list.chooseCategory')}>
              {props.crumb}
              {props.tone !== undefined && <ToneDot tone={props.tone} />}
              <span className="cx-c2">{props.title}</span>
              <span className="cx-cnt">{props.count}</span>
              <ChevronDown className="cx-ico" />
            </button>
          ) : (
            <>
              <div className="cx-crumb">{props.crumb}</div>
              <h2><span>{props.title}</span><span className="cx-cnt">{props.count}</span></h2>
            </>
          )}
        </div>
        <button type="button" className="cx-btn primary sm" onClick={props.onAdd} disabled={readOnly || !props.canAdd}
          title={readOnly ? props.lockTitle : t('catalog:list.addProductTitle')}>
          <Plus className="cx-ico" /> {t('catalog:list.addProduct')}
        </button>
      </div>

      {someChecked ? (
        <div className="cx-bulkbar" role="status">
          <input
            type="checkbox"
            className="cx-cb"
            aria-label={t('catalog:list.selectAll')}
            checked={allChecked}
            ref={el => { if (el) el.indeterminate = !allChecked; }}
            onChange={() => props.onSetChecked(allChecked ? [] : visibleKeys)}
          />
          <b>{t('catalog:list.checkedCount', { count: checked.length })}</b>
          {!allChecked && (
            <button type="button" className="cx-lnk" onClick={() => props.onSetChecked(Array.from(new Set(checked.concat(visibleKeys))))}>
              {t('catalog:list.selectAllCount', { count: visibleKeys.length })}
            </button>
          )}
          <span className="cx-sp" />
          <button type="button" className="cx-btn ghost sm" onClick={() => props.onSetChecked([])} aria-label={t('catalog:list.clearSelection')} title={t('catalog:list.clearSelection')}>
            <X className="cx-ico" /> <span className="cx-wide-only">{t('catalog:list.clearSelection')}</span>
          </button>
        </div>
      ) : (
        <div className="cx-lp-tools">
          <label className="cx-search">
            <Search className="cx-ico" aria-hidden="true" />
            <input
              className="cx-inp"
              type="search"
              autoComplete="off"
              value={props.search}
              onChange={e => props.onSearch(e.target.value)}
              placeholder={t('catalog:list.searchPlaceholder')}
              aria-label={t('catalog:list.searchPlaceholder')}
            />
          </label>
          <MenuButton
            label={t('catalog:filter.label')}
            className="cx-btn sm"
            align="right"
            kind="radio"
            items={(['all', 'available', 'unavailable', 'hidden'] as Filter[]).map(f => ({
              key: f, label: filterLabels[f], checked: props.filter === f, onSelect: () => props.onFilter(f),
            }))}
          >
            <ListFilter className="cx-ico" /> {filterLabels[props.filter]}
          </MenuButton>
        </div>
      )}

      <div className="cx-colhead" aria-hidden="true">
        <span>{someChecked ? (allChecked ? <Check className="cx-ico" /> : <Minus className="cx-ico" />) : null}</span>
        <span>{t('catalog:list.colProduct')}</span>
        <span className="cx-c-sizes">{t('catalog:list.colSizes')}</span>
        <span className="cx-c-groups">{t('catalog:list.colGroups')}</span>
        <span className="cx-c-av">{t('catalog:list.colAvailable')}</span>
        <span className="cx-c-eye" title={t('catalog:list.visibleTitle')}><Eye className="cx-ico" /></span>
      </div>

      <div className="cx-rows" role="list" aria-label={props.title}>
        {noRows ? (
          searching ? (
            <div className="cx-empty"><Search className="cx-ico" /><b>{t('catalog:list.noResults', { query: props.search.trim() })}</b><span>{t('catalog:list.noResultsHint')}</span></div>
          ) : (
            <div className="cx-empty"><Package className="cx-ico" /><b>{sections[0]?.empty || t('catalog:list.empty')}</b></div>
          )
        ) : sections.map(section => (
          <React.Fragment key={section.node || 'all'}>
            {section.title !== undefined && (
              <div className="cx-rgroup">
                <ToneDot tone={section.tone} />
                <span>{section.title}</span>
                <span>{section.count ?? section.rows.length}</span>
              </div>
            )}
            {section.rows.length === 0
              ? (section.title !== undefined && <div className="cx-rempty">{section.empty || t('catalog:list.empty')}</div>)
              : section.rows.map((v, i) => row(section, v, i))}
          </React.Fragment>
        ))}
      </div>

      <div className="cx-lp-foot">
        <span>
          {[
            t('catalog:list.footTotal', { count: props.footer.total }),
            props.footer.unavailable ? t('catalog:list.footUnavailable', { count: props.footer.unavailable }) : '',
            props.footer.hidden ? t('catalog:list.footHidden', { count: props.footer.hidden }) : '',
          ].filter(Boolean).join(' · ')}
        </span>
        <span className="cx-sp" />
        {props.reorderable && !readOnly && (
          <>
            <GripVertical className="cx-ico" aria-hidden="true" />
            <span title={t('catalog:list.reorderHint')}>{t('catalog:list.dragHint')}</span>
          </>
        )}
      </div>
    </section>
  );
};

export default ProductList;
